import { Tooltip } from '@material-ui/core';
import React from 'react';

import {
  ICustomRenderCellJSXProps,
  IListViewTableColumns,
  IListViewTableRow,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { useListViewTableStore } from '~/src/dash/components/tables/ListViewTable/context';
import { ListRender } from '~/src/dash/components/tables/ListViewTable/ListViewTableRow/templates';
import { Div, Text } from '~/src/dash/publicaUI';
import {
  IChannelGroupLabel,
  IChannelGroupRow,
  orderDirection,
} from '~/src/dash/views/channels/api';

export const formatDataToLVTRows = (
  data: IChannelGroupRow[],
  cfg: IChannelGroupsTable,
): IListViewTableRow[] => {
  const rows = [] as IListViewTableRow[];
  data.forEach((datum) => {
    const row = {} as IListViewTableRow;
    Object.values(cfg.columns).forEach((col) => {
      const { cellJSX, cellRenderType, cellStyle, formatValue, isNumeric, key, responseKey } = col;
      // ensure value is correct
      let value: any = datum[responseKey as keyof IChannelGroupRow];
      if (isNumeric) {
        value = value !== null ? Number(value) : null;
      }
      if (formatValue) {
        value = formatValue(value);
      }
      row[key] = {
        value,
        ...(cellJSX ? { jsx: cellJSX } : {}),
        ...(cellRenderType ? { renderType: cellRenderType } : {}),
        ...(cellStyle ? { cellStyle } : {}),
      };
    });
    rows.push(row);
  });
  return rows;
};

const ChannelGroupModalLink = (props: ICustomRenderCellJSXProps) => {
  const { customFunctions } = useListViewTableStore();
  const { value } = props;
  return (
    <Tooltip title={value} placement='top-start'>
      <Div onClick={() => customFunctions.handleClickOnName(props.row.ID.value)}>
        <Text
          small
          bold
          style={{
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            cursor: 'pointer',
          }}
        >
          {value}
        </Text>
      </Div>
    </Tooltip>
  );
};

const ChannelsModalLink = (props: ICustomRenderCellJSXProps) => {
  const { customFunctions } = useListViewTableStore();
  const { value } = props;
  return (
    <Div onClick={() => customFunctions.handleClickOnChannels(props.row.ChannelIDs.value)}>
      <Text small bold>
        {Array.isArray(value) ? value.length : 1}
      </Text>
    </Div>
  );
};

interface IChannelGroupsTable {
  columns: IListViewTableColumns;
}

export interface IChannelGroupsConfig {
  cypressClassName: string;
  defaultState: {
    activeColumnKeys: string[];
    isTableExpanded: boolean;
    orderDirection: orderDirection;
    orderBy: string;
    page: number;
    rowsPerPage: RowsPerPage;
    searchInput: string;
  };
  columnSelector: {
    resetKeys?: string[];
  };
  sorts: string[];
  table: IChannelGroupsTable;
}

export const channelGroupsConfig = {
  cypressClassName: 'channelsViewGroups',
  defaultState: {
    activeColumnKeys: ['ChannelGroupLabels', 'ChannelIDs'],
    isTableExpanded: false,
    orderDirection: 'desc',
    orderBy: 'name',
    page: 0,
    rowsPerPage: 25,
    searchInput: '',
  },
  enableClone: true,
  enableDelete: true,
  columnSelector: {
    resetKeys: ['ChannelGroupLabels', 'ChannelIDs'],
  },
  sorts: ['Name', 'ChannelGroupLabels', 'ChannelIDs'],
  table: {
    columns: {
      ID: {
        formatValue: (value: number) => value.toString(),
        isHidden: true,
        isSearchable: true,
        key: 'ID',
        label: 'ID',
        requestKey: 'ID',
        responseKey: 'ID',
        valueType: 'string',
      },
      Name: {
        cellJSX: ChannelGroupModalLink,
        cellRenderType: 'name_link',
        defaultOrderDirection: 'asc',
        headerRenderType: 'name',
        isSearchable: true,
        key: 'Name',
        label: 'Name',
        requestKey: 'name',
        responseKey: 'Name',
        valueType: 'string',
      },
      Actions: {
        cellRenderType: 'actions',
        disableSort: true,
        headerRenderType: 'actions',
        key: 'Actions',
        label: 'Actions',
        requestKey: '',
        responseKey: '',
        valueType: 'component',
      },
      ChannelIDs: {
        cellJSX: ChannelsModalLink,
        headerRenderType: 'number',
        cellRenderType: 'number',
        key: 'ChannelIDs',
        label: 'Channels',
        requestKey: 'channel_ids',
        responseKey: 'ChannelIDs',
        valueType: 'array',
      },
      ChannelGroupLabels: {
        cellJSX: ListRender,
        formatValue: (values) => values.map((value: IChannelGroupLabel) => value.Name),
        headerRenderType: 'string',
        cellRenderType: 'string',
        key: 'ChannelGroupLabels',
        label: 'Channel Labels',
        requestKey: 'channel_group_labels',
        responseKey: 'ChannelGroupLabels',
        valueType: 'array',
      },
    },
  },
} as IChannelGroupsConfig;
